import React from 'react';
import { useTranslation } from './i18n.js';

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];

function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '0:00';
  }
  const total = Math.floor(seconds);
  const minutes = Math.floor(total / 60);
  const secs = String(total % 60).padStart(2, '0');
  return `${minutes}:${secs}`;
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path d="M8 5v14l11-7z" fill="currentColor" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor" />
      <rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor" />
    </svg>
  );
}

function SpinnerIcon() {
  return <span className="audio-player__spinner" aria-hidden="true" />;
}

function StopIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
      <rect x="6" y="6" width="12" height="12" rx="1.5" fill="currentColor" />
    </svg>
  );
}

// Lecteur sans contrôles natifs ni lien de téléchargement : l'audio est joué via un objet Audio
// jamais inséré dans le DOM, ce qui évite le menu « Enregistrer l'audio sous » du navigateur.
// Le lecteur est affiché d'emblée mais l'audio n'est demandé (`loadSrc`, qui renvoie une URL)
// qu'au premier clic sur lecture : chaque synthèse vocale est facturée. Une nouvelle fonction
// `loadSrc` (changement de langue) remet le lecteur à zéro.
export function AudioPlayer({ loadSrc, defaultRate = 1 }) {
  const { t } = useTranslation();
  const audioRef = React.useRef(null);
  const abortRef = React.useRef(null);
  const [status, setStatus] = React.useState('idle');
  const [playing, setPlaying] = React.useState(false);
  // Audio reçu mais pas encore assez mis en mémoire pour démarrer (ou reprendre) la lecture.
  const [buffering, setBuffering] = React.useState(false);
  const [current, setCurrent] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [rate, setRate] = React.useState(defaultRate);
  const rateRef = React.useRef(rate);
  rateRef.current = rate;

  React.useEffect(() => {
    setStatus('idle');
    setPlaying(false);
    setBuffering(false);
    setCurrent(0);
    setDuration(0);

    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        URL.revokeObjectURL(audio.src);
        audioRef.current = null;
      }
    };
  }, [loadSrc]);

  React.useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
    }
  }, [rate]);

  const createAudio = (src) => {
    const audio = new Audio(src);
    audio.preload = 'auto';
    audio.playbackRate = rateRef.current;
    audio.addEventListener('loadedmetadata', () => {
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
      audio.playbackRate = rateRef.current;
    });
    audio.addEventListener('timeupdate', () => setCurrent(audio.currentTime));
    audio.addEventListener('play', () => setPlaying(true));
    audio.addEventListener('pause', () => {
      setPlaying(false);
      setBuffering(false);
    });
    audio.addEventListener('waiting', () => setBuffering(true));
    audio.addEventListener('playing', () => setBuffering(false));
    audio.addEventListener('ended', () => {
      setPlaying(false);
      audio.currentTime = 0;
      setCurrent(0);
    });
    return audio;
  };

  const loadAndPlay = async () => {
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus('loading');
    try {
      const src = await loadSrc(controller.signal);
      if (controller.signal.aborted) {
        URL.revokeObjectURL(src);
        return;
      }
      const audio = createAudio(src);
      audioRef.current = audio;
      setStatus('ready');
      setBuffering(true);
      audio.play().catch(() => {
        setPlaying(false);
        setBuffering(false);
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        setStatus(err.message === 'unavailable' ? 'unavailable' : 'error');
      }
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
    }
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) {
      if (status !== 'loading' && status !== 'unavailable') {
        loadAndPlay();
      }
      return;
    }
    if (audio.paused) {
      audio.play().catch(() => setPlaying(false));
    } else {
      audio.pause();
    }
  };

  const stop = () => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    audio.pause();
    audio.currentTime = 0;
    setCurrent(0);
  };

  const seek = (event) => {
    const audio = audioRef.current;
    const value = Number(event.target.value);
    if (audio) {
      audio.currentTime = value;
    }
    setCurrent(value);
  };

  const waiting = status === 'loading' || buffering;
  const mainLabel = waiting ? t('thoughtListenLoading') : playing ? t('audioPause') : t('audioPlay');
  const statusMessage = {
    error: t('thoughtListenError'),
    unavailable: t('thoughtListenUnavailable'),
  }[status];

  return (
    <div className="audio-player" onContextMenu={(event) => event.preventDefault()}>
      <button
        type="button"
        className={`audio-player__btn audio-player__btn--main${waiting ? ' is-loading' : ''}`}
        onClick={togglePlay}
        disabled={waiting || status === 'unavailable'}
        aria-label={mainLabel}
        title={mainLabel}
      >
        {waiting ? <SpinnerIcon /> : playing ? <PauseIcon /> : <PlayIcon />}
      </button>
      <button
        type="button"
        className="audio-player__btn"
        onClick={stop}
        disabled={status !== 'ready'}
        aria-label={t('audioStop')}
        title={t('audioStop')}
      >
        <StopIcon />
      </button>
      <input
        type="range"
        className="audio-player__seek"
        min={0}
        max={duration || 0}
        step={0.1}
        value={Math.min(current, duration || 0)}
        style={{ '--progress': `${duration ? (current / duration) * 100 : 0}%` }}
        onChange={seek}
        disabled={status !== 'ready'}
        aria-label={t('audioSeek')}
      />
      <span className="audio-player__time">
        {formatTime(current)} / {formatTime(duration)}
      </span>
      <select
        className="audio-player__speed"
        value={rate}
        onChange={(event) => setRate(Number(event.target.value))}
        aria-label={t('audioSpeed')}
      >
        {SPEEDS.map((speed) => (
          <option key={speed} value={speed}>
            {speed}×
          </option>
        ))}
      </select>
      {statusMessage && (
        <span className="audio-player__status" role="status">
          {statusMessage}
        </span>
      )}
    </div>
  );
}
